import type * as React from "react";
import { useCallback, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle } from "lucide-react";
import { Loader } from "@/ui/atoms";
import { DataTable, EmptyState } from "@/ui/molecules";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import {
    multiSelectParam,
    numberRangeParam,
    sortParam,
    textParam,
} from "@/useCases/serverTableQuery";
import { cn } from "@/utils/utils";
import type {
    TApiComparisonOutcomeFilter,
    TApiComparisonQuery,
    TApiComparisonResponse,
    TApiComparisonRow,
    TApiComparisonTotals,
} from "@/apis/proda_finance/types";
import { formatStatementAmount } from "./amountFormat";

const PAGE_SIZE = 50;
const TABLE_MAX_HEIGHT = 820;
// Same key shape as the review page's "file-review-line-items", so widths persist alike.
const STORAGE_KEY = "file-review-comparison";

export type TComparisonTabProps = {
    fileId: string;
    loadComparison: (
        fileId: string,
        query: TApiComparisonQuery,
        signal?: AbortSignal,
    ) => Promise<TApiComparisonResponse>;
};

type TLoadState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; data: TApiComparisonResponse; refetchFailed: boolean };

const OUTCOME_OPTIONS = [
    { value: "matched", label: "Matched" },
    { value: "mismatch", label: "Mismatch" },
    { value: "na", label: "N/A" },
];
const OUTCOME_VALUES: readonly string[] = OUTCOME_OPTIONS.map((o) => o.value);
const OPEN_TRIAL_BALANCE = "open_trial_balance";

type TTotalKey = keyof TApiComparisonTotals;

// Amount column id -> its [min, max] query keys (the line-items `amount_min/_max` shape).
type TAmountRangeKey = Extract<keyof TApiComparisonQuery, `${TTotalKey}_${"min" | "max"}`>;

const AMOUNT_PARAMS: Record<TTotalKey, [TAmountRangeKey, TAmountRangeKey]> = {
    prior_tb_closing: ["prior_tb_closing_min", "prior_tb_closing_max"],
    opening_balance: ["opening_balance_min", "opening_balance_max"],
    difference_past: ["difference_past_min", "difference_past_max"],
    gl_movement: ["gl_movement_min", "gl_movement_max"],
    expected_current_tb: ["expected_current_tb_min", "expected_current_tb_max"],
    current_tb_closing: ["current_tb_closing_min", "current_tb_closing_max"],
    difference_current: ["difference_current_min", "difference_current_max"],
};

const TEXT_PARAMS = ["account_code", "account_name", "property_code"] as const;

function outcomeParam(
    filters: TServerTableQueryInput["columnFilters"],
    id: string,
): TApiComparisonOutcomeFilter[] | undefined {
    const values = multiSelectParam(filters, id)?.filter((v) => OUTCOME_VALUES.includes(v));
    return values && values.length > 0 ? (values as TApiComparisonOutcomeFilter[]) : undefined;
}

function toQuery({
    columnFilters,
    sorting,
    page,
    pageSize,
}: TServerTableQueryInput): TApiComparisonQuery {
    const query: TApiComparisonQuery = { page, page_size: pageSize, ...sortParam(sorting) };
    const result = outcomeParam(columnFilters, "comparison_result");
    if (result) query.comparison_result = result;
    const past = outcomeParam(columnFilters, "compare_past");
    if (past) query.compare_past = past;
    for (const id of TEXT_PARAMS) {
        const value = textParam(columnFilters, id);
        if (value !== undefined) query[id] = value;
    }
    const books = multiSelectParam(columnFilters, "book_number")
        ?.map(Number)
        .filter((n) => Number.isInteger(n));
    if (books && books.length > 0) query.book_numbers = books;
    for (const id of Object.keys(AMOUNT_PARAMS) as TTotalKey[]) {
        const range = numberRangeParam(columnFilters, id);
        if (!range) continue;
        const [minKey, maxKey] = AMOUNT_PARAMS[id];
        if (range.min !== undefined) query[minKey] = range.min;
        if (range.max !== undefined) query[maxKey] = range.max;
    }
    return query;
}

function resultLabel(value: TApiComparisonRow["comparison_result"]): React.ReactElement {
    if (value === "mismatch") return <span className="font-medium text-red-600">Mismatch</span>;
    if (value === "matched") return <span className="font-medium text-violet-600">Matched</span>;
    return <span className="text-muted-foreground">N/A</span>;
}

const amountCell = (value: string | null): React.ReactElement => (
    <span className="font-mono text-xs text-right block">{formatStatementAmount(value)}</span>
);

const mono = (value: string | number): React.ReactElement => (
    <span className="font-mono text-xs">{value}</span>
);

const text = { filterType: "text" as const };
const numberRange = { filterType: "numberRange" as const };

/**
 * The beginning balance is Yardi's when the prior file is the deal's opening trial
 * balance (Yardi's Trial Balance report). For later months it is the property
 * manager's prior-month ending balance, so the columns say so rather than "Yardi".
 */
function beginningLabels(fromYardi: boolean): { beginning: string; ending: string } {
    return fromYardi
        ? { beginning: "Yardi Beginning Balance", ending: "Yardi Ending Balance" }
        : { beginning: "Prior PM Ending Balance", ending: "Expected Ending Balance" };
}

function buildColumns(
    openingBalanceReceived: boolean,
    bookNumbers: number[],
    beginningFromYardi: boolean,
    activityFromTb: boolean,
): ColumnDef<TApiComparisonRow>[] {
    const amount = (
        id: keyof TApiComparisonRow & TTotalKey,
        header: string,
    ): ColumnDef<TApiComparisonRow> => ({
        id,
        header,
        size: 120,
        minSize: 100,
        accessorFn: (r) => r[id],
        cell: ({ row }) => amountCell(row.original[id]),
        meta: numberRange,
    });
    const outcome = (
        id: "comparison_result" | "compare_past",
        header: string,
        size: number,
    ): ColumnDef<TApiComparisonRow> => ({
        id,
        header,
        size,
        minSize: 80,
        // compare_past is not a backend sort_by value, so it must not offer sorting.
        enableSorting: id === "comparison_result",
        accessorFn: (r) => r[id],
        cell: ({ row }) => <span className="text-xs">{resultLabel(row.original[id])}</span>,
        meta: { filterType: "multiSelect", filterOptions: OUTCOME_OPTIONS },
    });

    const beginning = beginningLabels(beginningFromYardi);
    return [
        {
            id: "account_code",
            header: "Acc No",
            size: 90,
            minSize: 70,
            accessorFn: (r) => r.account_code,
            cell: ({ row }) => mono(row.original.account_code),
            meta: text,
        },
        {
            id: "account_name",
            header: "Acc Name",
            size: 160,
            minSize: 100,
            accessorFn: (r) => r.account_name ?? "",
            cell: ({ row }) => (
                <div className="text-xs truncate" title={row.original.account_name || undefined}>
                    {row.original.account_name || "—"}
                </div>
            ),
            meta: text,
        },
        {
            id: "property_code",
            header: "Propcode",
            size: 90,
            minSize: 80,
            accessorFn: (r) => r.property_code,
            cell: ({ row }) => mono(row.original.property_code),
            meta: text,
        },
        {
            id: "book_number",
            header: "Book",
            size: 70,
            minSize: 50,
            accessorFn: (r) => r.book_number,
            cell: ({ row }) => mono(row.original.book_number),
            meta: {
                filterType: "multiSelect",
                filterOptions: bookNumbers.map((n) => ({ value: String(n), label: String(n) })),
            },
        },
        // A + B = C, compared with D: the order of LaSalle's Xceptor reconciliation.
        amount("gl_movement", activityFromTb ? "TB Activity Amount" : "GL Activity Amount"),
        amount("prior_tb_closing", beginning.beginning),
        amount("expected_current_tb", beginning.ending),
        outcome("comparison_result", "Recon Result", 130),
        {
            ...amount("opening_balance", "PM Beginning Balance"),
            cell: ({ row }) =>
                !openingBalanceReceived ? (
                    <span className="text-xs text-right block text-muted-foreground">
                        Not received
                    </span>
                ) : (
                    amountCell(row.original.opening_balance)
                ),
        },
        amount("current_tb_closing", "PM Ending Balance"),
        amount("difference_current", "Recon Difference"),
        outcome("compare_past", "Beginning Check", 120),
        amount("difference_past", "Beginning Difference"),
    ];
}

/** Column ids in render order, so the footer cells follow the (flat) column defs. */
function leafColumnIds(columns: ColumnDef<TApiComparisonRow>[]): string[] {
    return columns.map((c) => c.id ?? "");
}

function TotalsFooter({
    data,
    columnIds,
}: {
    data: TApiComparisonResponse;
    columnIds: string[];
}): React.ReactElement {
    const { totals, is_balanced } = data.summary;
    return (
        <tr className="font-semibold" data-testid="comparison-totals">
            {columnIds.map((id, index) => {
                const key: TTotalKey | null = id in totals ? (id as TTotalKey) : null;
                return (
                    <td
                        key={id}
                        className={cn(
                            "px-4 py-2 text-xs overflow-hidden",
                            key !== null && "text-right font-mono truncate",
                        )}
                    >
                        {index === 0 ? (
                            <div className="space-y-1 min-w-0">
                                <div>Total</div>
                                {!is_balanced && (
                                    <div
                                        className="flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-400"
                                        data-testid="comparison-not-balanced"
                                        title="Does not balance to 0"
                                    >
                                        <AlertTriangle className="h-3 w-3 shrink-0" />
                                        <span className="truncate">Does not balance to 0</span>
                                    </div>
                                )}
                            </div>
                        ) : key !== null ? (
                            key === "opening_balance" && !data.meta.opening_balance_received ? (
                                <span className="font-normal text-muted-foreground">
                                    Not received
                                </span>
                            ) : (
                                formatStatementAmount(totals[key])
                            )
                        ) : null}
                    </td>
                );
            })}
        </tr>
    );
}

function Note({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <p className="rounded-lg border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {children}
        </p>
    );
}

function summaryLine(data: TApiComparisonResponse): string {
    const { mismatched_count, matched_count, na_count } = data.summary;
    const count = (n: number, singular: string, plural: string): string =>
        n === 1 ? `1 row ${singular}` : `${n} rows ${plural}`;
    const base = `${count(mismatched_count, "is mismatching", "are mismatching")} · ${count(matched_count, "is matching", "are matching")}`;
    return na_count > 0 ? `${base} · ${na_count} N/A` : base;
}

/**
 * Trial Balance comparison in the terms of LaSalle's Xceptor reconciliation: GL
 * activity + Yardi beginning balance = expected Yardi ending balance, reconciled
 * against the property manager's ending balance. Without a GL the activity is the
 * TB's own (ending minus beginning balance). The PM's beginning balance is
 * also checked against the Yardi beginning balance. Filtering, sorting and paging are server-side;
 * the summary and totals always describe the whole file, not the visible page.
 */
export function ComparisonTab(props: TComparisonTabProps): React.ReactElement {
    const { fileId, loadComparison } = props;
    const [state, setState] = useState<TLoadState>({ status: "loading" });
    const [isRefetching, setIsRefetching] = useState(false);

    const fetchData = useCallback(
        (query: TApiComparisonQuery, signal?: AbortSignal) => {
            // Keep showing the previous data while a new query is in flight.
            setState((prev) =>
                prev.status === "ready" ? { ...prev, refetchFailed: false } : { status: "loading" },
            );
            setIsRefetching(true);
            loadComparison(fileId, query, signal)
                .then((data) => {
                    if (signal?.aborted) return;
                    setState({ status: "ready", data, refetchFailed: false });
                    setIsRefetching(false);
                })
                .catch(() => {
                    if (signal?.aborted) return;
                    // Keep already-shown data; only an initial-load failure replaces the tab.
                    setState((prev) =>
                        prev.status === "ready"
                            ? { ...prev, refetchFailed: true }
                            : { status: "error" },
                    );
                    setIsRefetching(false);
                });
        },
        [fileId, loadComparison],
    );

    const {
        page,
        setPage,
        sorting,
        onSortingChange,
        columnFilters,
        onColumnFiltersChange,
        refetch,
    } = useServerTable<TApiComparisonQuery>({
        pageSize: PAGE_SIZE,
        toQuery,
        fetchData,
    });

    const openingBalanceReceived =
        state.status === "ready" ? state.data.meta.opening_balance_received : true;
    const bookNumbers = useMemo(
        () => (state.status === "ready" ? state.data.meta.book_numbers : []),
        [state],
    );
    const bookKey = bookNumbers.join(",");
    // No prior file at all leaves the column N/A; it keeps Yardi's name.
    const beginningFromYardi =
        state.status !== "ready" ||
        state.data.meta.prior_file === null ||
        state.data.meta.prior_file.file_type === OPEN_TRIAL_BALANCE;
    // No GL for the period: the activity is the TB's ending minus opening balance.
    const activitySource = state.status === "ready" ? state.data.meta.activity_source : "gl";
    const activityFromTb = activitySource === "tb";
    const columns = useMemo(
        () => buildColumns(openingBalanceReceived, bookNumbers, beginningFromYardi, activityFromTb),
        // Keyed by the book list's value, so a refetch's fresh array does not rebuild columns.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openingBalanceReceived, bookKey, beginningFromYardi, activityFromTb],
    );
    const columnIds = useMemo(() => leafColumnIds(columns), [columns]);

    return (
        <div className="space-y-4" data-testid="comparison-tab">
            {state.status === "loading" ? (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            ) : state.status === "error" ? (
                <EmptyState
                    variant="error"
                    title="Couldn't load the comparison"
                    description="The comparison for this file could not be loaded."
                    action={{ label: "Try again", onClick: refetch }}
                />
            ) : (
                <div className="space-y-3">
                    {state.refetchFailed && (
                        <div
                            role="alert"
                            data-testid="comparison-refetch-error"
                            className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
                        >
                            <span>Couldn&apos;t refresh the comparison.</span>
                            <button
                                type="button"
                                onClick={refetch}
                                className="font-medium underline"
                            >
                                Try again
                            </button>
                        </div>
                    )}
                    <p className="text-sm font-medium" data-testid="comparison-summary">
                        {summaryLine(state.data)}
                    </p>
                    {state.data.meta.prior_file === null && (
                        <Note>
                            No beginning balance for this period (no Yardi Trial Balance or
                            prior-month TB) - the reconciliation is unavailable.
                        </Note>
                    )}
                    {!beginningFromYardi && (
                        <Note>
                            Yardi&apos;s Trial Balance is uploaded when the deal starts. For later
                            months the beginning balance is the property manager&apos;s prior-month
                            ending balance.
                        </Note>
                    )}
                    {activityFromTb && (
                        <Note>
                            No GL for this period - activity is the PM&apos;s Ending minus Beginning
                            Balance from the TB.
                        </Note>
                    )}
                    {activitySource === null && (
                        <Note>
                            No GL and no PM beginning balances for this period - the activity and
                            the reconciliation are unavailable.
                        </Note>
                    )}
                    {!state.data.meta.opening_balance_received && (
                        <Note>PM beginning balances were not received with this file.</Note>
                    )}
                    <div data-testid="comparison-table">
                        <DataTable
                            columns={columns}
                            data={state.data.items}
                            loading={isRefetching}
                            storageKey={STORAGE_KEY}
                            enableFiltering
                            enableColumnResizing
                            manualSorting
                            manualFiltering
                            sortingState={sorting}
                            onSortingChange={onSortingChange}
                            columnFilters={columnFilters}
                            onColumnFiltersChange={onColumnFiltersChange}
                            maxHeight={TABLE_MAX_HEIGHT}
                            className="border rounded-lg"
                            showRowCount={false}
                            emptyMessage="No accounts match the current filters."
                            rowClassName={(r) =>
                                r.comparison_result === "mismatch"
                                    ? "bg-red-50/60 even:bg-red-50/60 hover:bg-red-100/60 dark:bg-red-950/30 dark:even:bg-red-950/30 dark:hover:bg-red-950/50"
                                    : undefined
                            }
                            footerRow={<TotalsFooter data={state.data} columnIds={columnIds} />}
                            pagination={{
                                page,
                                pageSize: PAGE_SIZE,
                                total: state.data.total,
                                onPageChange: setPage,
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
